import { useState, useEffect, useRef } from 'react'
import { X, User, Mail, Phone, Trophy, AlertCircle, CheckCircle, Upload, FileImage, ExternalLink } from 'lucide-react'
import { registerTeam } from '../services/registrationService'
import { trackTournamentRegistration, trackRegistrationStep, trackPaymentProofUpload, trackRegistrationError } from '../config/analytics'

function RegistrationModal({ 
  isOpen, 
  onClose, 
  category, 
  tournament, 
  currentUser, 
  userProfile,
  themeClasses,
  colorTheme,
  isDarkMode 
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [paymentProof, setPaymentProof] = useState(null)
  const [paymentProofPreview, setPaymentProofPreview] = useState(null)
  const [isPlayer1Editable, setIsPlayer1Editable] = useState(false)
  const [showSizeGuide, setShowSizeGuide] = useState(false)
  const [sizeGuideImage, setSizeGuideImage] = useState('')
  const errorRef = useRef(null)
  const firstInputRef = useRef(null)
  const modalContentRef = useRef(null)
  
  const numberOfPlayers = category?.numberOfPlayers || 2
  const totalSteps = numberOfPlayers + 2 // Players + Payment Proof + Summary
  
  // Initialize player data
  const [players, setPlayers] = useState([
    {
      firstName: '',
      lastName: '',
      email: '',
      mobileNumber: '',
      duprId: '',
      duprRating: '',
      shirtStyle: 'Unisex',
      shirtSize: '',
      userId: currentUser?.uid || null
    }
  ])

  // Auto-scroll to error message when error occurs
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [error])

  // Auto-focus first input field when step changes, or scroll to top for payment/summary
  useEffect(() => {
    if (!loading && !success) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // If on payment or summary step, scroll modal content to top
        if (currentStep === numberOfPlayers + 1 || currentStep === numberOfPlayers + 2) {
          if (modalContentRef.current) {
            modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
          }
        } 
        // Otherwise focus first input field
        else if (firstInputRef.current) {
          firstInputRef.current?.focus()
          firstInputRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          })
        }
      }, 100)
    }
  }, [currentStep, loading, success, numberOfPlayers])

  // Reset form and pre-fill player 1 with user profile data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset all state
      setCurrentStep(1)
      setError('')
      setSuccess(false)
      setPaymentProof(null)
      setPaymentProofPreview(null)
      setLoading(false)
      
      // Initialize with user profile data
      if (currentUser && userProfile) {
        let firstName = userProfile.firstName || '';
        let lastName = userProfile.lastName || '';
        let editable = false;
        
        // If firstName or lastName not available, parse from displayName
        if (!firstName || !lastName) {
          const displayName = currentUser.displayName || '';
          if (displayName) {
            const nameParts = displayName.trim().split(' ');
            firstName = nameParts[0] || '';
            lastName = nameParts.slice(1).join(' ') || '';
          }
          // Make fields editable if parsed from displayName
          editable = true;
        }
        
        setIsPlayer1Editable(editable);
        setPlayers([{
          firstName: firstName,
          lastName: lastName,
          email: currentUser.email || '',
          mobileNumber: userProfile.phoneNumber || '',
          duprId: userProfile.duprId || '',
          duprRating: userProfile.duprRating ? userProfile.duprRating.toString() : '',
          shirtStyle: 'Unisex',
          shirtSize: '',
          userId: currentUser.uid
        }])
      } else {
        // Reset to empty if no user profile
        setIsPlayer1Editable(true);
        setPlayers([{
          firstName: '',
          lastName: '',
          email: '',
          mobileNumber: '',
          duprId: '',
          duprRating: '',
          shirtStyle: 'Unisex',
          shirtSize: '',
          userId: currentUser?.uid || null
        }])
      }
    }
  }, [isOpen, currentUser, userProfile])

  const validateMobileNumber = (number) => {
    if (!number || !number.trim()) {
      return true // Empty is valid (optional field)
    }
    
    // Remove all spaces, dashes, and parentheses
    const cleaned = number.replace(/[\s\-()]/g, '')
    
    // Philippine format with +63: Must be exactly 13 characters (+63 + 10 digits)
    if (cleaned.startsWith('+63')) {
      return /^\+63\d{10}$/.test(cleaned)
    }
    
    // Philippine local format starting with 09: Must be exactly 11 digits
    if (cleaned.startsWith('09')) {
      return /^09\d{9}$/.test(cleaned)
    }
    
    // International format (non-Philippine): + followed by country code and number
    // Require minimum 10 digits total (country code + number)
    if (cleaned.startsWith('+')) {
      return /^\+\d{10,15}$/.test(cleaned)
    }
    
    // Other local formats: Require exactly 10-11 digits (common for most countries)
    return /^\d{10,11}$/.test(cleaned)
  }

  const handlePlayerChange = (index, field, value) => {
    const newPlayers = [...players]
    newPlayers[index] = {
      ...newPlayers[index],
      [field]: value
    }
    setPlayers(newPlayers)
  }

  const handlePaymentProofUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file')
        return
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size must be less than 5MB')
        return
      }
      setPaymentProof(file)
      setPaymentProofPreview(URL.createObjectURL(file))
      setError('')
      // Track payment proof upload
      trackPaymentProofUpload(tournament?.id || 'unknown')
    }
  }

  const validatePlayer = (player, playerNumber) => {
    if (!player.firstName.trim()) {
      return `Player ${playerNumber} first name is required`
    }
    if (!player.lastName.trim()) {
      return `Player ${playerNumber} last name is required`
    }
    if (!player.email.trim() && !player.mobileNumber.trim()) {
      return `Player ${playerNumber} must have either email or mobile number`
    }
    if (player.email.trim() && !player.email.includes('@')) {
      return `Player ${playerNumber} email is invalid`
    }
    if (player.mobileNumber.trim() && !validateMobileNumber(player.mobileNumber)) {
      return `Player ${playerNumber} mobile number is invalid. Please enter a complete number (e.g., +63 912 345 6789 or 09123456789)`
    }
    return null
  }

  const handleNext = () => {
    setError('')
    
    // Player information steps
    if (currentStep <= numberOfPlayers) {
      const playerIndex = currentStep - 1
      const validationError = validatePlayer(players[playerIndex], playerIndex + 1)
      if (validationError) {
        setError(validationError)
        return
      }
      
      // Validate shirt size
      if (!players[playerIndex].shirtSize) {
        setError(`Please select a shirt size for Player ${playerIndex + 1}`)
        return
      }
      
      // Add next player if not exists
      if (playerIndex < numberOfPlayers - 1 && players.length < playerIndex + 2) {
        setPlayers(prev => [...prev, {
          firstName: '',
          lastName: '',
          email: '',
          mobileNumber: '',
          duprId: '',
          duprRating: '',
          shirtStyle: 'Unisex',
          shirtSize: '',
          userId: null
        }])
      }
      
      setCurrentStep(currentStep + 1)
      trackRegistrationStep(currentStep + 1, totalSteps)
    }
    // Payment proof step
    else if (currentStep === numberOfPlayers + 1) {
      if (!paymentProof) {
        setError('Please upload proof of payment')
        return
      }
      setCurrentStep(currentStep + 1) // Go to summary
      trackRegistrationStep(currentStep + 1, totalSteps)
    }
    // Summary/confirmation step - submit
    else if (currentStep === numberOfPlayers + 2) {
      handleSubmit()
    }
  }

  const handleBack = () => {
    setError('')
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (loading) {
      return
    }

    // Validate all players
    for (let i = 0; i < numberOfPlayers; i++) {
      const validationError = validatePlayer(players[i], i + 1)
      if (validationError) {
        setError(validationError)
        setCurrentStep(i + 1)
        return
      }
    }

    setLoading(true)
    setError('')

    try {
      const result = await registerTeam({
        tournamentId: tournament.id,
        categoryId: category.id,
        player1: players[0],
        player2: numberOfPlayers > 1 ? players[1] : null,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: userProfile ? `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() : '',
        paymentProof: paymentProof
      })

      if (result.success) {
        // Track successful tournament registration
        trackTournamentRegistration(
          tournament?.id || 'unknown',
          category?.id || 'unknown',
          numberOfPlayers
        )
        setSuccess(true)
        setLoading(false)
        setTimeout(() => {
          onClose()
          // Reset state
          setSuccess(false)
          setCurrentStep(1)
          setPlayers([{
            firstName: '',
            lastName: '',
            email: '',
            mobileNumber: '',
            duprId: '',
            duprRating: '',
            shirtStyle: 'Unisex',
            shirtSize: '',
            userId: currentUser?.uid || null
          }])
          setPaymentProof(null)
          setPaymentProofPreview(null)
        }, 2000)
      } else {
        setLoading(false)
        setError(result.error || 'Registration failed. Please contact the tournament organizers for assistance.')
        console.error('Registration error:', result.error)
        // Track registration error
        trackRegistrationError(result.error || 'Registration failed. Please contact the tournament organizers for assistance.')
      }
    } catch (err) {
      console.error('Registration error:', err)
      const errorMessage = err.message || 'An error occurred during registration. Please contact the tournament organizers for assistance.'
      setError(errorMessage)
      // Track registration error
      trackRegistrationError(errorMessage)
      setLoading(false)
    }
  }

  const handleBackdropClick = () => {
    // Don't allow closing if registration is in progress or successful
    if (loading || success) {
      return
    }

    // If email is not verified, close without confirmation (user can't register anyway)
    if (!currentUser?.emailVerified) {
      onClose()
      return
    }

    // Show confirmation dialog
    const confirmClose = window.confirm(
      'Are you sure you want to exit? Your registration progress will be lost.'
    )
    
    if (confirmClose) {
      onClose()
    }
  }

  if (!isOpen) return null

  const currentPlayer = players[currentStep - 1] || {}

  return (
    <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/70' : 'bg-black/50'} backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200`} onClick={handleBackdropClick}>
      <div 
        className={`${themeClasses.bg} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-all duration-300 border ${themeClasses.border} scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-gray-500 animate-in zoom-in-95 fade-in duration-300`}
        onClick={(e) => e.stopPropagation()}
        style={{ scrollbarWidth: 'thin' }}
      >
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-white via-gray-50 to-white'} border-b ${themeClasses.border} p-8 sticky top-0 z-10 backdrop-blur-xl`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20' : 'bg-gradient-to-br from-emerald-50 to-teal-50'} flex items-center justify-center border ${isDarkMode ? 'border-emerald-500/30' : 'border-emerald-200'}`}>
                  <Trophy className={`w-6 h-6 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <h2 className={`text-2xl sm:text-3xl font-black ${themeClasses.text} tracking-tight`}>Tournament Registration</h2>
                </div>
              </div>
              <div className={`${isDarkMode ? 'bg-white/5' : 'bg-gray-100/50'} rounded-lg px-4 py-3 border ${themeClasses.border}`}>
                <p className={`text-sm font-semibold ${themeClasses.text} mb-1`}>{tournament.name}</p>
                <p className={`text-sm ${themeClasses.textMuted}`}>{category.title}</p>
                {(category.skillLevel || category.ageGroup) && (
                  <p className={`text-xs ${themeClasses.textMuted} mt-1.5 flex items-center gap-2`}>
                    {category.skillLevel && (
                      <span className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-100 text-emerald-700'} text-xs font-medium`}>
                        {category.skillLevel}
                      </span>
                    )}
                    {category.ageGroup && category.ageGroup !== 'Open' && (
                      <span className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-100 text-blue-700'} text-xs font-medium`}>
                        {category.ageGroup}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleBackdropClick}
              className={`p-2.5 ${themeClasses.hoverBg} rounded-xl transition-all duration-300 hover:scale-110 ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
            >
              <X className={`w-5 h-5 ${themeClasses.text}`} />
            </button>
          </div>
        </div>

        {/* Email Verification Required Warning */}
        {!currentUser?.emailVerified && (
          <div className="p-6">
            <div className="bg-yellow-500/10 border-2 border-yellow-500/30 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className={`text-lg font-bold mb-2 ${themeClasses.text}`}>Email Verification Required</h3>
                  <p className={`${themeClasses.textMuted} text-sm mb-3`}>
                    You must verify your email address before registering for tournaments. Please check your inbox for a verification email.
                  </p>
                  <p className={`${themeClasses.textMuted} text-xs`}>
                    Didn't receive the email? Check your spam folder or contact support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="p-6">
            <div className="bg-emerald-500/10 border-2 border-emerald-500/30 rounded-xl p-6 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h3 className={`text-xl font-bold mb-2 ${themeClasses.text}`}>Registration Successful!</h3>
              <p className={themeClasses.textMuted}>Your team has been registered for the tournament.</p>
            </div>
          </div>
        )}

        {/* Form */}
        {!success && currentUser?.emailVerified && (
          <div className="p-6">
            {/* Progress Indicator */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-semibold ${themeClasses.textMuted}`}>
                  {currentStep <= numberOfPlayers && `Player ${currentStep} of ${numberOfPlayers}`}
                  {currentStep === numberOfPlayers + 1 && 'Payment'}
                  {currentStep === numberOfPlayers + 2 && 'Review & Confirm'}
                </span>
                <span className={`text-sm font-semibold ${colorTheme.text.primary}`}>
                  {Math.round((currentStep / totalSteps) * 100)}%
                </span>
              </div>
              <div className={`h-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                <div
                  className={`h-full ${isDarkMode ? 'bg-emerald-500' : 'bg-emerald-500'} transition-all duration-300`}
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            {/* Step Title */}
            {currentStep <= numberOfPlayers && (
              <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${themeClasses.text}`}>
                <User className="w-5 h-5" />
                Player {currentStep} Information
                {currentStep === 1 && <span className="text-sm font-normal opacity-60">(You)</span>}
              </h3>
            )}
            {currentStep === numberOfPlayers + 1 && (
              <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${themeClasses.text}`}>
                <Upload className="w-5 h-5" />
                Upload Proof of Payment
              </h3>
            )}
            {currentStep === numberOfPlayers + 2 && (
              <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${themeClasses.text}`}>
                <CheckCircle className="w-5 h-5" />
                Review & Confirm Registration
              </h3>
            )}

            {/* Error Message */}
            {error && (
              <div ref={errorRef} className="mb-4 bg-red-500/10 border-2 border-red-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-400">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm font-semibold">{error}</p>
                </div>
              </div>
            )}

            {/* Player Information Form Fields */}
            {currentStep <= numberOfPlayers && (
            <div className="space-y-4">
              {/* First Name & Last Name - Same Line */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${themeClasses.text}`}>
                    First Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    ref={firstInputRef}
                    type="text"
                    value={currentPlayer.firstName || ''}
                    onChange={(e) => handlePlayerChange(currentStep - 1, 'firstName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300 ${currentStep === 1 && !isPlayer1Editable ? 'opacity-60 cursor-not-allowed' : ''}`}
                    placeholder="First name"
                    disabled={loading || (currentStep === 1 && !isPlayer1Editable)}
                    readOnly={currentStep === 1 && !isPlayer1Editable}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${themeClasses.text}`}>
                    Last Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={currentPlayer.lastName || ''}
                    onChange={(e) => handlePlayerChange(currentStep - 1, 'lastName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300 ${currentStep === 1 && !isPlayer1Editable ? 'opacity-60 cursor-not-allowed' : ''}`}
                    placeholder="Last name"
                    disabled={loading || (currentStep === 1 && !isPlayer1Editable)}
                    readOnly={currentStep === 1 && !isPlayer1Editable}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${themeClasses.text}`}>
                  Email Address {!currentPlayer.mobileNumber && <span className="text-red-400">*</span>}
                </label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${themeClasses.textMuted}`} />
                  <input
                    type="email"
                    value={currentPlayer.email || ''}
                    onChange={(e) => handlePlayerChange(currentStep - 1, 'email', e.target.value)}
                    className={`w-full pl-11 pr-4 py-3 rounded-lg ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300 ${currentStep === 1 ? 'opacity-60 cursor-not-allowed' : ''}`}
                    placeholder="player@example.com"
                    disabled={loading || currentStep === 1}
                    readOnly={currentStep === 1}
                  />
                </div>
              </div>

              {/* Mobile Number */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${themeClasses.text}`}>
                  Mobile Number {!currentPlayer.email && <span className="text-red-400">*</span>}
                </label>
                <div className="relative">
                  <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${themeClasses.textMuted}`} />
                  <input
                    type="tel"
                    value={currentPlayer.mobileNumber || ''}
                    onChange={(e) => handlePlayerChange(currentStep - 1, 'mobileNumber', e.target.value)}
                    className={`w-full pl-11 pr-4 py-3 rounded-lg ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300`}
                    placeholder="+63 912 345 6789"
                    disabled={loading}
                  />
                </div>
                {currentPlayer.mobileNumber && !validateMobileNumber(currentPlayer.mobileNumber) && (
                  <p className="text-xs text-red-400 mt-1">
                    Please enter a complete mobile number (e.g., +63 912 345 6789 or 09123456789)
                  </p>
                )}
              </div>

              {/* DUPR ID & Rating - Same Line */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${themeClasses.text}`}>
                    DUPR ID <span className={`text-xs font-normal ${themeClasses.textMuted}`}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={currentPlayer.duprId || ''}
                    onChange={(e) => handlePlayerChange(currentStep - 1, 'duprId', e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300`}
                    placeholder="DUPR ID"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${themeClasses.text}`}>
                    DUPR Rating <span className={`text-xs font-normal ${themeClasses.textMuted}`}>(Optional)</span>
                  </label>
                  <div className="relative">
                    <Trophy className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${themeClasses.textMuted}`} />
                    <input
                      type="number"
                      step="0.01"
                      value={currentPlayer.duprRating || ''}
                      onChange={(e) => handlePlayerChange(currentStep - 1, 'duprRating', e.target.value)}
                      className={`w-full pl-11 pr-4 py-3 rounded-lg ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300`}
                      placeholder="0.00"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Shirt Size Selection - Integrated in Player Form */}
              <div className={`mt-6 pt-6 border-t ${themeClasses.border}`}>
                <h4 className={`text-md font-bold mb-4 ${themeClasses.text}`}>Tournament Shirt</h4>
                
                {/* Shirt Style Selection */}
                <div className="mb-4">
                  <label className={`block text-sm font-semibold mb-3 ${themeClasses.text}`}>
                    Shirt Style <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handlePlayerChange(currentStep - 1, 'shirtStyle', 'Female')}
                      className={`px-6 py-4 rounded-lg font-semibold transition-all duration-300 ${
                        currentPlayer.shirtStyle === 'Female'
                          ? 'bg-emerald-500 text-white border-2 border-emerald-500'
                          : `${themeClasses.inputBg} ${themeClasses.text} border-2 ${themeClasses.inputBorder} hover:border-emerald-500/50`
                      }`}
                      disabled={loading}
                    >
                      Female
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePlayerChange(currentStep - 1, 'shirtStyle', 'Unisex')}
                      className={`px-6 py-4 rounded-lg font-semibold transition-all duration-300 ${
                        currentPlayer.shirtStyle === 'Unisex'
                          ? 'bg-emerald-500 text-white border-2 border-emerald-500'
                          : `${themeClasses.inputBg} ${themeClasses.text} border-2 ${themeClasses.inputBorder} hover:border-emerald-500/50`
                      }`}
                      disabled={loading}
                    >
                      Unisex
                    </button>
                  </div>
                  
                  {/* Size Guide Link */}
                  <div className="mt-3 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        const imageUrl = currentPlayer.shirtStyle === 'Female' 
                          ? 'https://thesmashcity.com/wp-content/uploads/2026/01/big-c-female-size-chart.jpeg'
                          : 'https://thesmashcity.com/wp-content/uploads/2026/01/big-c-unisex-size-chart.jpeg'
                        setSizeGuideImage(imageUrl)
                        setShowSizeGuide(true)
                      }}
                      className={`inline-flex items-center gap-2 text-sm font-semibold ${colorTheme.text.primary} hover:underline transition-all`}
                    >
                      <ExternalLink className="w-4 h-4" />
                      View {currentPlayer.shirtStyle} Size Guide
                    </button>
                  </div>
                </div>

                {/* Shirt Size Selection */}
                <div>
                  <label className={`block text-sm font-semibold mb-3 ${themeClasses.text}`}>
                    Shirt Size <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'].map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => handlePlayerChange(currentStep - 1, 'shirtSize', size)}
                        className={`px-4 py-3 rounded-lg font-semibold transition-all duration-300 ${
                          currentPlayer.shirtSize === size
                            ? 'bg-emerald-500 text-white border-2 border-emerald-500'
                            : `${themeClasses.inputBg} ${themeClasses.text} border-2 ${themeClasses.inputBorder} hover:border-emerald-500/50`
                        }`}
                        disabled={loading}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selected Summary */}
                {currentPlayer.shirtSize && (
                  <div className={`${themeClasses.cardBg} border ${themeClasses.border} rounded-lg p-4 mt-4`}>
                    <p className={`text-sm ${themeClasses.text}`}>
                      <span className="font-semibold">Selected:</span> {currentPlayer.shirtStyle} - Size {currentPlayer.shirtSize}
                    </p>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Payment Proof Upload Step */}
            {currentStep === numberOfPlayers + 1 && (
              <div className="space-y-4">
                {/* Payment QR Code Display */}
                {tournament?.paymentQrCodeUrl && (
                  <div className={`${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg p-8 min-h-[400px] flex flex-col justify-center`}>
                    <h3 className={`text-sm font-semibold mb-4 ${themeClasses.text}`}>
                      Payment Details
                    </h3>
                    <div className="flex flex-col items-center">
                      <img 
                        src={tournament.paymentQrCodeUrl} 
                        alt="Payment QR Code" 
                        className="max-w-[240px] w-full rounded-lg border-2 border-emerald-500/30 shadow-lg"
                      />
                      <p className={`text-sm ${themeClasses.textMuted} mt-4 text-center max-w-sm`}>
                        Scan this QR code to make payment
                      </p>
                      <p className={`text-xs ${themeClasses.textMuted} mt-2 text-center`}>
                        After payment, scroll down to upload your receipt
                      </p>
                    </div>
                  </div>
                )}

                <div className={`${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg p-6`}>
                  <label className={`block text-sm font-semibold mb-4 ${themeClasses.text}`}>
                    Proof of Payment <span className="text-red-400">*</span>
                  </label>
                  <div className="space-y-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePaymentProofUpload}
                      className="hidden"
                      id="payment-proof-upload"
                      disabled={loading}
                    />
                    <label
                      htmlFor="payment-proof-upload"
                      className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed ${themeClasses.inputBorder} rounded-lg cursor-pointer ${themeClasses.hoverBg} transition-colors`}
                    >
                      {paymentProofPreview ? (
                        <img src={paymentProofPreview} alt="Payment proof preview" className="max-h-44 rounded" />
                      ) : (
                        <div className="flex flex-col items-center">
                          <FileImage className={`w-12 h-12 mb-2 ${themeClasses.textMuted}`} />
                          <p className={`text-sm ${themeClasses.text}`}>Click to upload payment proof</p>
                          <p className={`text-xs ${themeClasses.textMuted} mt-1`}>PNG, JPG up to 5MB</p>
                        </div>
                      )}
                    </label>
                    {paymentProof && (
                      <div className={`flex items-center gap-2 ${themeClasses.text} text-sm`}>
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        <span>{paymentProof.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Summary/Confirmation Step */}
            {currentStep === numberOfPlayers + 2 && (
              <div className="space-y-4">
                <div className={`${themeClasses.cardBg} border ${themeClasses.border} rounded-lg p-6 space-y-4`}>
                  <h4 className={`font-bold ${themeClasses.text}`}>Registration Summary</h4>
                  
                  {/* Players Summary */}
                  {players.map((player, index) => (
                    <div key={index} className={`border-t ${themeClasses.border} pt-4`}>
                      <p className={`font-semibold ${themeClasses.text} mb-2`}>Player {index + 1}</p>
                      <div className={`text-sm ${themeClasses.textMuted} space-y-1`}>
                        <p><span className="font-medium">Name:</span> {player.firstName} {player.lastName}</p>
                        {player.email && <p><span className="font-medium">Email:</span> {player.email}</p>}
                        {player.mobileNumber && <p><span className="font-medium">Mobile:</span> {player.mobileNumber}</p>}
                        {player.duprId && <p><span className="font-medium">DUPR ID:</span> {player.duprId}</p>}
                        {player.duprRating && <p><span className="font-medium">DUPR Rating:</span> {player.duprRating}</p>}
                        <p><span className="font-medium">Shirt:</span> {player.shirtStyle} - Size {player.shirtSize}</p>
                      </div>
                    </div>
                  ))}

                  {/* Payment Proof Summary */}
                  <div className={`border-t ${themeClasses.border} pt-4`}>
                    <p className={`font-semibold ${themeClasses.text} mb-2`}>Payment Proof</p>
                    {paymentProofPreview && (
                      <img src={paymentProofPreview} alt="Payment proof" className="max-h-32 rounded border ${themeClasses.border}" />
                    )}
                  </div>

                  {/* Entry Fee */}
                  {category.entryFee && (
                    <div className={`border-t ${themeClasses.border} pt-4`}>
                      <p className={`font-semibold ${themeClasses.text} mb-2`}>Entry Fee Breakdown</p>
                      <div className={`text-sm ${themeClasses.textMuted} space-y-1`}>
                        <p>₱{category.entryFee.toLocaleString()} per player × {numberOfPlayers} player{numberOfPlayers > 1 ? 's' : ''}</p>
                        <p className={`text-lg font-bold ${themeClasses.text} mt-2`}>Total: ₱{(category.entryFee * numberOfPlayers).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`bg-blue-500/10 border-2 border-blue-500/30 rounded-lg p-4`}>
                  <p className={`text-sm ${themeClasses.text}`}>
                    Please review your information carefully. Once submitted, your registration will be processed by the tournament organizers.
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              {currentStep > 1 && (
                <button
                  onClick={handleBack}
                  disabled={loading}
                  className={`flex-1 px-6 py-3 rounded-lg font-bold ${themeClasses.cardBg} border ${themeClasses.border} ${themeClasses.text} hover:bg-opacity-80 transition-all duration-300 disabled:opacity-50`}
                >
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={loading}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50"
              >
                {loading ? 'Processing...' : 
                  currentStep < numberOfPlayers ? 'Next Player' : 
                  currentStep === numberOfPlayers ? 'Continue to Payment' :
                  currentStep === numberOfPlayers + 1 ? 'Review Registration' :
                  'Confirm & Submit'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowSizeGuide(false)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowSizeGuide(false)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all z-10"
              aria-label="Close size guide"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={sizeGuideImage} 
              alt="Size Guide" 
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default RegistrationModal
